import {
  AdditionalFilters,
  ComputePolicyViolationsCountRequest,
  FailedPolicyEvaluationStats,
  FailedPolicyEvaluationStatsRequest,
  GetMetricResultsRequest,
  GetPolicyViolationsRequest,
  MetricCreate,
  MetricEdit,
  MetricResults,
  PolicyCreate,
  GetPoliciesRequest,
  GetPoliciesResponse,
  PolicyEdit,
  PolicyExecutionStats,
  PolicyExecutionStatsRequest,
  PolicyTestRequestBodyInput,
  PolicyViolationOutcome,
  PolicyViolations,
  PolicyViolationsCount,
  PolicyViolationsCountMap,
  Status,
  TopFailedPoliciesRequest,
  TopFailedPolicyInsight,
  GetAllResourceTypesResponse,
  GetMetricsResponse,
  GetMetricsRequest,
} from '@agilelab/plugin-wb-governance-common';
import {
  FindResourceStatusRequest,
  FindResourceStatusResponse,
} from '@agilelab/plugin-wb-governance-common';

import {
  encodeQueryParams,
  handleFailedResponse,
} from '@agilelab/plugin-wb-platform-common';
import { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import crossFetch from 'cross-fetch';
import type {
  ManagedPolicyCreatePayload,
  ManagedPolicyDetail,
  ManagedPolicyEditPayload,
} from './components/EnvironmentPromotionPolicyWizard/types';

export declare type GovernanceRequestOptions = {
  token?: string;
};

export interface GovernanceApi {
  createPolicy(
    policyData: PolicyCreate,
    options?: GovernanceRequestOptions,
  ): Promise<string>;

  updatePolicy(
    id: string,
    policyData: PolicyEdit,
    options?: GovernanceRequestOptions,
  ): Promise<string>;

  updatePolicyStatus(
    id: string,
    status: Status,
    options?: GovernanceRequestOptions,
  ): Promise<void>;

  createNewPolicyVersion(
    id: string,
    options?: GovernanceRequestOptions,
  ): Promise<void>;

  launchTestPolicy(
    id: string,
    policyTestRequestBodyInput?: PolicyTestRequestBodyInput,
    options?: GovernanceRequestOptions,
  ): Promise<{ reportId: string }>;

  createMetric(
    metricData: MetricCreate,
    options?: GovernanceRequestOptions,
  ): Promise<string>;

  updateMetric(
    id: string,
    metricData: any,
    options?: GovernanceRequestOptions,
  ): Promise<string>;

  updateMetricStatus(
    id: string,
    status: Status,
    options?: GovernanceRequestOptions,
  ): Promise<void>;

  createNewMetricVersion(
    id: string,
    options?: GovernanceRequestOptions,
  ): Promise<void>;

  launchTestMetric(
    id: string,
    additionalFilters?: AdditionalFilters,
    options?: GovernanceRequestOptions,
  ): Promise<{ reportId: string }>;

  getTopFailedPolicies(
    params: TopFailedPoliciesRequest,
    options?: GovernanceRequestOptions,
  ): Promise<TopFailedPolicyInsight[]>;

  getPolicyExecutionStats(
    params: PolicyExecutionStatsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<PolicyExecutionStats[]>;

  getFailedPolicyEvaluationStats(
    params: FailedPolicyEvaluationStatsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<FailedPolicyEvaluationStats[]>;

  computePolicyViolationsCount(
    params: ComputePolicyViolationsCountRequest,
    options?: GovernanceRequestOptions,
  ): Promise<PolicyViolationsCountMap>;

  getPolicyViolations(
    params: GetPolicyViolationsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<PolicyViolations>;

  getPolicies(
    params: GetPoliciesRequest,
    options?: GovernanceRequestOptions,
  ): Promise<GetPoliciesResponse>;

  getMetrics(
    params: GetMetricsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<GetMetricsResponse>;

  getMetricResults(
    params: GetMetricResultsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<MetricResults>;

  findResourceEvaluationStatus(
    params: FindResourceStatusRequest,
    options?: GovernanceRequestOptions,
  ): Promise<FindResourceStatusResponse>;

  getAllResourceTypes(
    options?: GovernanceRequestOptions,
  ): Promise<GetAllResourceTypesResponse>;

  createManagedPolicy(
    payload: ManagedPolicyCreatePayload,
    options?: GovernanceRequestOptions,
  ): Promise<string>;

  editManagedPolicy(
    id: string,
    payload: ManagedPolicyEditPayload,
    options?: GovernanceRequestOptions,
  ): Promise<void>;

  getManagedPolicies(
    params: { type?: string },
    options?: GovernanceRequestOptions,
  ): Promise<ManagedPolicyDetail[]>;

  // `id` may be either the opaque managed policy id or the id of any individual
  // governance entity belonging to it (e.g. from the generic policies list) — the
  // platform resolves the association internally. Returns null on 404.
  getManagedPolicy(
    id: string,
    options?: GovernanceRequestOptions,
  ): Promise<ManagedPolicyDetail | null>;

  updateManagedPolicyStatus(
    id: string,
    status: Status,
    options?: GovernanceRequestOptions,
  ): Promise<void>;
}

export class GovernanceClient implements GovernanceApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly baseUrlPromise: Promise<string>;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi?: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.baseUrlPromise = this.discoveryApi.getBaseUrl('governance');
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
  }

  private async getCredentials(
    options?: GovernanceRequestOptions,
  ): Promise<string> {
    return options?.token ?? '';
  }

  async getPolicies(
    params: GetPoliciesRequest,
    options?: GovernanceRequestOptions,
  ): Promise<GetPoliciesResponse> {
    const baseUrl = await this.baseUrlPromise;

    const {
      'id-in': ids,
      limit,
      offset,
      env,
      text,
      context,
      status,
      strategy,
      engine,
      'resource-type': resourceTypes,
      severity,
      timing,
      trigger,
      'managed-policy-type': managedPolicyType,
      'sort-by': sortBy,
      'sort-order': sortOrder,
    } = params;

    const queryParams = encodeQueryParams({
      'id-in': ids,
      env,
      text,
      context,
      status,
      strategy,
      engine,
      'resource-type': resourceTypes,
      severity,
      timing,
      trigger,
      'managed-policy-type': managedPolicyType,
      limit,
      offset,
      'sort-by': sortBy,
      'sort-order': sortOrder,
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies${queryParams}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
        },
      },
    );

    await handleFailedResponse(response);
    const result = await response.json();

    return result;
  }

  async createPolicy(
    policyData: PolicyCreate,
    options?: GovernanceRequestOptions,
  ): Promise<string> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(`${baseUrl}/policies`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(policyData),
    });
    await handleFailedResponse(response);

    return response.json();
  }

  async updatePolicy(
    id: string,
    policyData: PolicyEdit,
    options?: GovernanceRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(`${baseUrl}/policies/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(policyData),
    });
    await handleFailedResponse(response);

    return response.json();
  }

  async updatePolicyStatus(
    id: string,
    status: Status,
    options?: GovernanceRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/${id}/status`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async createNewPolicyVersion(
    id: string,
    options?: GovernanceRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/${id}/new-version`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async launchTestPolicy(
    id: string,
    policyTestRequestBodyInput?: PolicyTestRequestBodyInput,
    options?: GovernanceRequestOptions,
  ): Promise<{ reportId: string }> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/${id}/test`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(policyTestRequestBodyInput),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async getMetrics(
    params: GetMetricsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<GetMetricsResponse> {
    const baseUrl = await this.baseUrlPromise;

    const {
      'id-in': ids,
      limit,
      offset,
      env,
      text,
      context,
      status,
      strategy,
      'resource-type': resourceTypes,
      timing,
      trigger,
      'sort-by': sortBy,
      'sort-order': sortOrder,
    } = params;

    const queryParams = encodeQueryParams({
      'id-in': ids,
      env,
      text,
      context,
      status,
      strategy,
      'resource-type': resourceTypes,
      timing,
      trigger,
      limit,
      offset,
      'sort-by': sortBy,
      'sort-order': sortOrder,
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/metrics${queryParams}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
        },
      },
    );

    await handleFailedResponse(response);
    const result = await response.json();

    return result;
  }

  async createMetric(
    metricData: MetricCreate,
    options?: GovernanceRequestOptions | undefined,
  ): Promise<string> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(`${baseUrl}/metrics`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metricData),
    });
    await handleFailedResponse(response);

    return response.json();
  }

  async updateMetric(
    id: string,
    metricData: MetricEdit,
    options?: GovernanceRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(`${baseUrl}/metrics/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metricData),
    });
    await handleFailedResponse(response);

    return response.json();
  }

  async updateMetricStatus(
    id: string,
    status: Status,
    options?: GovernanceRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/metrics/${id}/status`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async createNewMetricVersion(
    id: string,
    options?: GovernanceRequestOptions,
  ): Promise<any> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/metrics/${id}/new-version`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async launchTestMetric(
    id: string,
    additionalFilters: AdditionalFilters,
    options?: GovernanceRequestOptions | undefined,
  ): Promise<{ reportId: string }> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/metrics/${id}/test`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(additionalFilters),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async getTopFailedPolicies(
    params: TopFailedPoliciesRequest,
    options?: GovernanceRequestOptions,
  ): Promise<TopFailedPolicyInsight[]> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/stats/top-failed`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async getPolicyExecutionStats(
    params: PolicyExecutionStatsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<PolicyExecutionStats[]> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/stats/executions`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async getFailedPolicyEvaluationStats(
    params: FailedPolicyEvaluationStatsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<FailedPolicyEvaluationStats[]> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/stats/executions/failures`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    );
    await handleFailedResponse(response);

    return response.json();
  }

  async computePolicyViolationsCount(
    params: ComputePolicyViolationsCountRequest,
    options?: GovernanceRequestOptions,
  ): Promise<PolicyViolationsCountMap> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/resources/policy-violations/count`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    );
    await handleFailedResponse(response);

    const policyViolationsCount =
      (await response.json()) as PolicyViolationsCount;

    const result: PolicyViolationsCountMap = new Map();

    policyViolationsCount.items.forEach(item => {
      const resourceMap: Map<PolicyViolationOutcome, number> = new Map();
      item.violations.forEach(violation => {
        resourceMap.set(violation.outcome, violation.count);
      });
      result.set(item.resource, resourceMap);
    });

    return result;
  }

  async getPolicyViolations(
    params: GetPolicyViolationsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<PolicyViolations> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = encodeQueryParams({
      env: params.environment,
      policies: params.policies?.join(','),
      timing: params.timing,
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/resources/${params.resource}/policy-violations${queryParams}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getMetricResults(
    params: GetMetricResultsRequest,
    options?: GovernanceRequestOptions,
  ): Promise<MetricResults> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = encodeQueryParams({
      env: params.environment,
      metrics: params.metrics?.join(','),
      timing: params.timing,
    });
    const response = await this.fetchApi.fetch(
      `${baseUrl}/resources/${params.resource}/metric-results${queryParams}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async findResourceEvaluationStatus(
    params: FindResourceStatusRequest,
    options?: GovernanceRequestOptions,
  ): Promise<FindResourceStatusResponse> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/resources/evaluation-status/search-query`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getAllResourceTypes(
    options?: GovernanceRequestOptions,
  ): Promise<GetAllResourceTypesResponse> {
    const baseUrl = await this.baseUrlPromise;
    const response = await this.fetchApi.fetch(`${baseUrl}/resource-types`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
      },
    });
    await handleFailedResponse(response);
    return response.json();
  }

  async createManagedPolicy(
    payload: ManagedPolicyCreatePayload,
    options?: GovernanceRequestOptions,
  ): Promise<string> {
    const baseUrl = await this.baseUrlPromise;
    // API: POST /v1/computational-governance/policies/managed
    const response = await this.fetchApi.fetch(`${baseUrl}/policies/managed`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    await handleFailedResponse(response);
    // Body may be empty on 202 — parse defensively
    const text = await response.text();
    if (!text.trim()) return '';
    try {
      const result = JSON.parse(text);
      return result.id ?? result;
    } catch {
      return text;
    }
  }

  async editManagedPolicy(
    id: string,
    payload: ManagedPolicyEditPayload,
    options?: GovernanceRequestOptions,
  ): Promise<void> {
    const baseUrl = await this.baseUrlPromise;
    // API: PUT /v1/computational-governance/policies/managed/{id}
    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/managed/${id}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );
    await handleFailedResponse(response);
    // Consume body to avoid connection leaks (may be empty)
    await response.text().catch(() => undefined);
  }

  async getManagedPolicies(
    params: { type?: string },
    options?: GovernanceRequestOptions,
  ): Promise<ManagedPolicyDetail[]> {
    const baseUrl = await this.baseUrlPromise;
    // API: GET /v1/computational-governance/policies/managed
    // Response: { managedPolicies: [...] } — managed policies only, never custom ones.
    const response = await this.fetchApi.fetch(`${baseUrl}/policies/managed`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${await this.getCredentials(options)}`,
      },
    });
    await handleFailedResponse(response);
    const result = await response.json();
    const all: ManagedPolicyDetail[] = (result.managedPolicies ?? []).map(
      (p: any): ManagedPolicyDetail => ({
        ...p,
        isManaged: true,
      }),
    );
    // Filter by type if provided
    if (params.type) {
      return all.filter(p => p.type === params.type);
    }
    return all;
  }

  async getManagedPolicy(
    id: string,
    options?: GovernanceRequestOptions,
  ): Promise<ManagedPolicyDetail | null> {
    const baseUrl = await this.baseUrlPromise;
    // API: GET /v1/computational-governance/policies/managed/{id}
    // `id` may be the opaque managed policy id or the id of any individual governance
    // entity belonging to it — resolved server-side.
    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/managed/${id}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
        },
      },
    );
    if (response.status === 404) {
      return null;
    }
    await handleFailedResponse(response);
    const result = await response.json();
    return { ...result, isManaged: true };
  }

  async updateManagedPolicyStatus(
    id: string,
    status: Status,
    options?: GovernanceRequestOptions,
  ): Promise<void> {
    const baseUrl = await this.baseUrlPromise;
    // API: PUT /v1/computational-governance/policies/managed/{id}/status
    const response = await this.fetchApi.fetch(
      `${baseUrl}/policies/managed/${id}/status`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await this.getCredentials(options)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      },
    );
    await handleFailedResponse(response);
    await response.text().catch(() => undefined);
  }
}
